/**
 * Canonical key-name vocabulary for the desktop-dictation hotkey (SCR-4020).
 *
 * The wire format is an ordered array of canonical key names (modifiers first,
 * optional terminal key last). This module owns:
 *   - the canonical name tuples per platform (used for validation + types),
 *   - the display formatter that turns a chord into a human-readable label
 *     (macOS glyphs joined with no separator; Windows abbreviations joined with `+`).
 *
 * Platform keycode → canonical name mapping lives on the Rust side
 * (`key_names.rs`); the FE only needs the names. See
 * `docs/desktop/dictation/HOTKEY.md` for the full design.
 */

import type { IntlShape, MessageDescriptor } from 'react-intl';
import {
  encodeChord,
  MOD_ORDER,
  type DictationHotkeyPlatform,
  type HotkeyCombo,
} from '../onboarding/hotkey/utils';

import { platformKeyMessages } from './platformKeyNames.messages';

// Moved to `@heidi/dictate` (SCR-7069); re-exported so existing consumers
// keep working without each one chasing the import.
export { encodeChord, MOD_ORDER, type HotkeyCombo };

// --------------------------------------------------------------------------
// Type vocabulary
// --------------------------------------------------------------------------

/**
 * macOS modifier names — sided per SCR-4113, named per the W3C
 * `KeyboardEvent.code` standard. `MetaLeft`/`MetaRight` are Cmd, and
 * `AltLeft`/`AltRight` are Option (W3C uses the physical-key-position name).
 * `Fn` has no left/right counterpart on Apple keyboards.
 *
 * Single-sourced from a literal `as const` tuple so the type union and the
 * runtime `Set` below never drift.
 */
const MAC_MODIFIERS = [
  'Fn',
  'ControlLeft',
  'ControlRight',
  'AltLeft',
  'AltRight',
  'ShiftLeft',
  'ShiftRight',
  'MetaLeft',
  'MetaRight',
] as const;
export type MacModifier = (typeof MAC_MODIFIERS)[number];

/**
 * Windows modifier names — sided per SCR-4113, named per the W3C
 * `KeyboardEvent.code` standard. `MetaLeft`/`MetaRight` are Win. Generic
 * `Control` / `Shift` / `Alt` / `Meta` are intentionally **not** valid:
 * chords are always recorded with the exact side the user pressed.
 */
const WINDOWS_MODIFIERS = [
  'ControlLeft',
  'ControlRight',
  'AltLeft',
  'AltRight',
  'ShiftLeft',
  'ShiftRight',
  'MetaLeft',
  'MetaRight',
] as const;
export type WindowsModifier = (typeof WINDOWS_MODIFIERS)[number];

// --------------------------------------------------------------------------
// macOS canonical name vocabulary.
// --------------------------------------------------------------------------

/**
 * W3C `KeyboardEvent.code` names that the macOS hook reports. The vocabulary
 * is the W3C UI Events code standard — the same physical-key identifiers
 * browsers emit on every keystroke and the Rust `keyboard-types` crate's
 * `Code` enum. Sided modifier names (`ControlLeft`, `MetaRight`, …) come for
 * free with W3C.
 *
 * Used for runtime validation (`isMacKeyName`) and to derive `MacKeyName`.
 * The platform keycode (CGKeyCode) → name mapping lives in
 * `src-tauri/src/global_hotkey/key_names.rs` (`code_to_mac_keycode`); the FE
 * receives names directly on the `hotkey:key_captured` event.
 */
export const MAC_KEY_NAMES = [
  // --- Modifiers --------------------------------------------------------
  'Fn',
  'CapsLock',
  'MetaLeft',
  'MetaRight',
  'ShiftLeft',
  'ShiftRight',
  'AltLeft',
  'AltRight',
  'ControlLeft',
  'ControlRight',

  // --- Letters ----------------------------------------------------------
  'KeyA',
  'KeyB',
  'KeyC',
  'KeyD',
  'KeyE',
  'KeyF',
  'KeyG',
  'KeyH',
  'KeyI',
  'KeyJ',
  'KeyK',
  'KeyL',
  'KeyM',
  'KeyN',
  'KeyO',
  'KeyP',
  'KeyQ',
  'KeyR',
  'KeyS',
  'KeyT',
  'KeyU',
  'KeyV',
  'KeyW',
  'KeyX',
  'KeyY',
  'KeyZ',

  // --- Digits (top row) -------------------------------------------------
  'Digit0',
  'Digit1',
  'Digit2',
  'Digit3',
  'Digit4',
  'Digit5',
  'Digit6',
  'Digit7',
  'Digit8',
  'Digit9',

  // --- Function keys ----------------------------------------------------
  'F1',
  'F2',
  'F3',
  'F4',
  'F5',
  'F6',
  'F7',
  'F8',
  'F9',
  'F10',
  'F11',
  'F12',
  'F13',
  'F14',
  'F15',
  'F16',
  'F17',
  'F18',
  'F19',
  'F20',

  // --- Arrows -----------------------------------------------------------
  'ArrowLeft',
  'ArrowRight',
  'ArrowDown',
  'ArrowUp',

  // --- Whitespace / editing --------------------------------------------
  'Space',
  'Tab',
  'Enter',
  'Backspace',
  'Delete',
  'Escape',

  // --- Navigation -------------------------------------------------------
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Help',

  // --- Punctuation (US ANSI) -------------------------------------------
  'Backquote',
  'Minus',
  'Equal',
  'BracketLeft',
  'BracketRight',
  'Backslash',
  'Semicolon',
  'Quote',
  'Comma',
  'Period',
  'Slash',

  // --- ISO --------------------------------------------------------------
  'IntlBackslash', // § on ISO layouts

  // --- JIS --------------------------------------------------------------
  'IntlYen',
  'IntlRo',
  'Lang1', // Kana
  'Lang2', // Eisu

  // --- Numpad -----------------------------------------------------------
  'Numpad0',
  'Numpad1',
  'Numpad2',
  'Numpad3',
  'Numpad4',
  'Numpad5',
  'Numpad6',
  'Numpad7',
  'Numpad8',
  'Numpad9',
  'NumpadDecimal',
  'NumpadMultiply',
  'NumpadAdd',
  'NumpadSubtract',
  'NumpadDivide',
  'NumpadEnter',
  'NumpadEqual',
  'NumpadClear',
  'NumpadComma',
] as const;

export type MacKeyName = (typeof MAC_KEY_NAMES)[number];

// --------------------------------------------------------------------------
// Windows canonical name vocabulary.
//
// Platform keycode (VK) → name mapping lives in
// `src-tauri/src/global_hotkey/key_names.rs` (`code_to_windows_vk`); the FE
// receives names directly on the `hotkey:key_captured` event.
// --------------------------------------------------------------------------

export const WINDOWS_KEY_NAMES = [
  // --- Modifiers (sided) ----------------------------------------------
  'ControlLeft',
  'ControlRight',
  'ShiftLeft',
  'ShiftRight',
  'AltLeft',
  'AltRight',
  'MetaLeft',
  'MetaRight',

  // --- Letters ----------------------------------------------------------
  'KeyA',
  'KeyB',
  'KeyC',
  'KeyD',
  'KeyE',
  'KeyF',
  'KeyG',
  'KeyH',
  'KeyI',
  'KeyJ',
  'KeyK',
  'KeyL',
  'KeyM',
  'KeyN',
  'KeyO',
  'KeyP',
  'KeyQ',
  'KeyR',
  'KeyS',
  'KeyT',
  'KeyU',
  'KeyV',
  'KeyW',
  'KeyX',
  'KeyY',
  'KeyZ',

  // --- Digits (top row) -------------------------------------------------
  'Digit0',
  'Digit1',
  'Digit2',
  'Digit3',
  'Digit4',
  'Digit5',
  'Digit6',
  'Digit7',
  'Digit8',
  'Digit9',

  // --- Function keys ----------------------------------------------------
  'F1',
  'F2',
  'F3',
  'F4',
  'F5',
  'F6',
  'F7',
  'F8',
  'F9',
  'F10',
  'F11',
  'F12',
  'F13',
  'F14',
  'F15',
  'F16',
  'F17',
  'F18',
  'F19',
  'F20',
  'F21',
  'F22',
  'F23',
  'F24',

  // --- Arrows -----------------------------------------------------------
  'ArrowLeft',
  'ArrowUp',
  'ArrowRight',
  'ArrowDown',

  // --- Whitespace / editing --------------------------------------------
  'Space',
  'Tab',
  'Enter',
  'Backspace',
  'Delete',
  'Insert',
  'Escape',

  // --- Navigation -------------------------------------------------------
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'ContextMenu',
  'PrintScreen',

  // --- Locks ------------------------------------------------------------
  'CapsLock',
  'NumLock',
  'ScrollLock',
  'Pause',

  // --- Punctuation (US ANSI) -------------------------------------------
  'Semicolon',
  'Equal',
  'Comma',
  'Minus',
  'Period',
  'Slash',
  'Backquote',
  'BracketLeft',
  'Backslash',
  'BracketRight',
  'Quote',
  'IntlBackslash', // OEM 102 (ISO layouts)

  // --- Numpad -----------------------------------------------------------
  'Numpad0',
  'Numpad1',
  'Numpad2',
  'Numpad3',
  'Numpad4',
  'Numpad5',
  'Numpad6',
  'Numpad7',
  'Numpad8',
  'Numpad9',
  'NumpadMultiply',
  'NumpadAdd',
  'NumpadComma',
  'NumpadSubtract',
  'NumpadDecimal',
  'NumpadDivide',
] as const;

export type WindowsKeyName = (typeof WINDOWS_KEY_NAMES)[number];

// --------------------------------------------------------------------------
// Validation
// --------------------------------------------------------------------------

const MAC_NAMES = new Set<string>(MAC_KEY_NAMES);
const WINDOWS_NAMES = new Set<string>(WINDOWS_KEY_NAMES);

const MAC_MODIFIER_SET: ReadonlySet<MacModifier> = new Set(MAC_MODIFIERS);
const WINDOWS_MODIFIER_SET: ReadonlySet<WindowsModifier> = new Set(WINDOWS_MODIFIERS);

export const isMacKeyName = (name: string): name is MacKeyName => MAC_NAMES.has(name);
export const isWindowsKeyName = (name: string): name is WindowsKeyName =>
  WINDOWS_NAMES.has(name);
export const isMacModifier = (name: string): name is MacModifier =>
  MAC_MODIFIER_SET.has(name as MacModifier);
export const isWindowsModifier = (name: string): name is WindowsModifier =>
  WINDOWS_MODIFIER_SET.has(name as WindowsModifier);

/** Returns true iff every entry is a valid canonical name for the platform. */
export const isValidChordForPlatform = (
  platform: DictationHotkeyPlatform,
  names: string[]
): boolean => {
  if (names.length === 0) return false;
  const guard =
    platform === 'mac' ? isMacKeyName : platform === 'windows' ? isWindowsKeyName : null;
  if (!guard) return true; // web layer uses TanStack format; not our concern here.
  return names.every((name: string) => guard(name));
};

/**
 * Resolve a stored chord against the canonical vocabulary, falling back to
 * the platform default when the value is missing, empty, or contains any
 * unknown name. Web values are passed through as-is.
 */
export const resolveStoredChord = (
  platform: DictationHotkeyPlatform,
  stored: string[] | null | undefined,
  defaults: Record<DictationHotkeyPlatform, readonly string[]>
): string[] => {
  if (!Array.isArray(stored) || stored.length === 0) return [...defaults[platform]];
  if (platform === 'web') return [...stored];
  return isValidChordForPlatform(platform, stored)
    ? [...stored]
    : [...defaults[platform]];
};

// --------------------------------------------------------------------------
// Encode / decode helpers
// --------------------------------------------------------------------------

/**
 * Decode a wire-format chord array into `{ modifiers, key }`. Assumes the
 * array is already valid (callers should call `isValidChordForPlatform` if
 * the source is untrusted). The last entry is the terminal key iff it is not
 * a recognised modifier for the platform; otherwise the whole array is treated
 * as modifier-only.
 */
export const decodeChord = (
  platform: DictationHotkeyPlatform,
  chord: string[]
): HotkeyCombo => {
  const noop = (): boolean => false;
  const isMod =
    platform === 'mac'
      ? isMacModifier
      : platform === 'windows'
        ? isWindowsModifier
        : noop;
  if (chord.length === 0) return { modifiers: [], key: null };
  const last = chord[chord.length - 1] as string;
  if (isMod(last)) return { modifiers: [...chord], key: null };
  return { modifiers: chord.slice(0, -1), key: last };
};

// --------------------------------------------------------------------------
// Display
// --------------------------------------------------------------------------

type FormatMessage = IntlShape['formatMessage'];

/** Resolve a display value that is either a literal string or a message. */
const resolveOverride = (
  formatMessage: FormatMessage,
  value: string | MessageDescriptor
): string => (typeof value === 'string' ? value : formatMessage(value));

/** macOS modifier glyphs keyed by the sideless root name. */
const MAC_MODIFIER_GLYPH_BY_ROOT: Record<string, string> = {
  Meta: '⌘',
  Alt: '⌥',
  Control: '⌃',
  Shift: '⇧',
};

/**
 * Windows modifier base labels keyed by sideless root. `Shift` uses the ⇧ glyph
 * like macOS; `Ctrl` is translatable (e.g. `Strg` in German); `Alt`/`Win` are
 * universal abbreviations kept literal.
 */
const WINDOWS_MODIFIER_BASE: Record<string, string | MessageDescriptor> = {
  Control: platformKeyMessages.ctrl,
  Alt: 'Alt',
  Meta: 'Win',
  Shift: '⇧',
};

/** Resolve the translated side word for a sided modifier name. */
const sideLabel = (formatMessage: FormatMessage, name: string): string =>
  formatMessage(
    name.endsWith('Right') ? platformKeyMessages.right : platformKeyMessages.left
  );

/**
 * macOS modifier label. Sided modifiers render as glyph + space + side label
 * (e.g. `⇧ Left`, `⌃ Right`) per the SCR-4113 design call. `Fn` has no side
 * and is always rendered lowercase.
 */
const formatMacModifier = (formatMessage: FormatMessage, token: MacModifier): string => {
  if (token === 'Fn') return 'fn';
  const root = stripModifierSide(token);
  return `${MAC_MODIFIER_GLYPH_BY_ROOT[root] ?? ''} ${sideLabel(formatMessage, token)}`;
};

/**
 * Windows modifier label. Sided modifiers use side-first ordering
 * (e.g. `Left Ctrl`, `Right Alt`). Joined with `+` per Windows convention.
 */
const formatWindowsModifier = (
  formatMessage: FormatMessage,
  token: WindowsModifier
): string => {
  const root = stripModifierSide(token);
  const base = WINDOWS_MODIFIER_BASE[root] ?? root;
  return `${sideLabel(formatMessage, token)} ${resolveOverride(formatMessage, base)}`;
};

/**
 * macOS terminal-key display labels. Most names render verbatim; whitespace
 * and a handful of punctuation keys use friendlier glyphs. Word entries are
 * translatable messages; glyphs/punctuation/`Esc` stay literal.
 */
const MAC_KEY_DISPLAY_OVERRIDES: Partial<Record<MacKeyName, string | MessageDescriptor>> =
  {
    Space: platformKeyMessages.space,
    Tab: platformKeyMessages.tab,
    Enter: '⏎',
    Backspace: '⌫',
    Delete: platformKeyMessages.forwardDelete,
    Escape: 'Esc',
    ArrowLeft: '←',
    ArrowRight: '→',
    ArrowUp: '↑',
    ArrowDown: '↓',
    PageUp: platformKeyMessages.pageUp,
    PageDown: platformKeyMessages.pageDown,
    Home: platformKeyMessages.home,
    End: platformKeyMessages.end,
    Help: platformKeyMessages.help,
    CapsLock: '⇪',
    Backquote: '`',
    Minus: '-',
    Equal: '=',
    BracketLeft: '[',
    BracketRight: ']',
    Backslash: '\\',
    Semicolon: ';',
    Quote: "'",
    Comma: ',',
    Period: '.',
    Slash: '/',
    IntlBackslash: '§',
    IntlYen: '¥',
  };

const WINDOWS_KEY_DISPLAY_OVERRIDES: Partial<
  Record<WindowsKeyName, string | MessageDescriptor>
> = {
  Space: platformKeyMessages.space,
  Tab: platformKeyMessages.tab,
  Enter: '⏎',
  Backspace: '⌫',
  Delete: platformKeyMessages.delete,
  Insert: platformKeyMessages.insert,
  Escape: 'Esc',
  ArrowLeft: '←',
  ArrowRight: '→',
  ArrowUp: '↑',
  ArrowDown: '↓',
  PageUp: platformKeyMessages.pageUp,
  PageDown: platformKeyMessages.pageDown,
  Home: platformKeyMessages.home,
  End: platformKeyMessages.end,
  ContextMenu: platformKeyMessages.contextMenu,
  Pause: platformKeyMessages.pause,
  CapsLock: '⇪',
  NumLock: platformKeyMessages.numLock,
  ScrollLock: platformKeyMessages.scrollLock,
  PrintScreen: platformKeyMessages.printScreen,
  Backquote: '`',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  IntlBackslash: '\\',
};

/**
 * Strip W3C `KeyboardEvent.code` prefixes from character keys so the user
 * sees `C` instead of `KeyC` and `3` instead of `Digit3`. Numpad keys keep
 * their prefix because the distinction is meaningful at display time.
 */
const stripCharacterKeyPrefix = (token: string): string => {
  if (token.length === 4 && token.startsWith('Key')) return token.slice(3);
  if (token.length === 6 && token.startsWith('Digit')) return token.slice(5);
  return token;
};

/**
 * Render a single chord token as a display string. Used by both the chord
 * formatter and the kbd-chip renderers.
 */
export const formatChordToken = (
  token: string,
  platform: DictationHotkeyPlatform,
  formatMessage: FormatMessage
): string => {
  if (platform === 'mac') {
    if (isMacModifier(token)) return formatMacModifier(formatMessage, token);
    if (isMacKeyName(token)) {
      const override = MAC_KEY_DISPLAY_OVERRIDES[token];
      return override !== undefined
        ? resolveOverride(formatMessage, override)
        : stripCharacterKeyPrefix(token);
    }
    return stripCharacterKeyPrefix(token);
  }
  if (platform === 'windows') {
    if (isWindowsModifier(token)) return formatWindowsModifier(formatMessage, token);
    if (isWindowsKeyName(token)) {
      const override = WINDOWS_KEY_DISPLAY_OVERRIDES[token];
      return override !== undefined
        ? resolveOverride(formatMessage, override)
        : stripCharacterKeyPrefix(token);
    }
    return stripCharacterKeyPrefix(token);
  }
  // Web — display as-is.
  return token;
};

/**
 * Render an ordered chord array as **an array of display tokens**
 * (one per chord member). Used by chord renderers that draw a sequence of
 * `<Kbd>` chips, one per token.
 */
export const formatChordTokens = (
  chord: string[],
  platform: DictationHotkeyPlatform,
  formatMessage: FormatMessage
): string[] =>
  chord.map((t: string): string => formatChordToken(t, platform, formatMessage));

// --------------------------------------------------------------------------
// Side-prefix utilities
// --------------------------------------------------------------------------

/**
 * Strip the `Left` / `Right` suffix from a sided W3C modifier name, returning
 * the generic root (`Control`, `Shift`, `Alt`, `Meta`). Other names (`Fn`,
 * letters, function keys, etc.) pass through unchanged.
 *
 * Used by the reserved-combo denylist so a single entry like `Alt+F4` matches
 * both `AltLeft+F4` and `AltRight+F4`.
 */
export const stripModifierSide = (name: string): string => {
  for (const suffix of ['Left', 'Right'] as const) {
    if (name.endsWith(suffix)) {
      const root = name.slice(0, -suffix.length);
      if (root === 'Control' || root === 'Shift' || root === 'Alt' || root === 'Meta') {
        return root;
      }
    }
  }
  return name;
};

/**
 * Apply `stripModifierSide` to every token in a chord. Returns a new array so
 * callers can hash / compare against denylist entries without mutating state.
 */
export const stripChordSides = (chord: readonly string[]): string[] =>
  chord.map(stripModifierSide);
